import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FiCheck, FiX, FiAlertCircle, FiPackage, FiUser, FiClock, FiFilter } from 'react-icons/fi';
import request from '../utils/request';
import { formatDate } from '../utils/formatDate';

export default function RefundApprovalQueue({ orders = [], completedOrders = [], onRefundUpdated }) {
  const { adminInfo } = useSelector(s => s.login);
  const isAdmin = adminInfo?.isAdmin || adminInfo?.role === 'admin';
  
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [processingId, setProcessingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(null);

  // Collect all pending refunds from all orders
  const refundItems = useMemo(() => {
    const allOrders = [...orders, ...completedOrders];
    const items = [];

    allOrders.forEach(order => {
      (order.products || []).forEach((product, idx) => {
        if (product.refunded) {
          items.push({
            orderId: order._id,
            orderUniqueId: order.uniqueId,
            productIndex: idx,
            product,
            order,
            refundedAt: product.refundedAt,
            refundedByName: product.refundedByName,
            refundReason: product.refundReason,
            refundNotes: product.refundNotes,
            refundApproved: product.refundApproved,
            refundRejected: product.refundRejected,
            refundApprovedByName: product.refundApprovedByName,
            refundRejectionReason: product.refundRejectionReason,
          });
        }
      });
    });

    // Sort by refundedAt, newest first
    items.sort((a, b) => new Date(b.refundedAt) - new Date(a.refundedAt));

    return items;
  }, [orders, completedOrders]);

  // Filter items based on selected filter
  const filteredItems = useMemo(() => {
    switch (filter) {
      case 'pending':
        return refundItems.filter(item => item.refundApproved === undefined || item.refundApproved === null);
      case 'approved':
        return refundItems.filter(item => item.refundApproved === true);
      case 'rejected':
        return refundItems.filter(item => item.refundApproved === false);
      default:
        return refundItems;
    }
  }, [refundItems, filter]);

  // Handle approve refund
  const handleApprove = async (item) => {
    setProcessingId(`${item.orderId}-${item.productIndex}`);
    try {
      const approverName = `${adminInfo.firstName} ${adminInfo.lastName}`;
      await request.patch(`/orders/${item.orderId}/products/${item.productIndex}/approve-refund`, {
        refundApproved: true,
        refundApprovedBy: adminInfo._id,
        refundApprovedByName: approverName,
        refundApprovedAt: new Date().toISOString(),
      });
      
      if (onRefundUpdated) {
        onRefundUpdated(item.orderId, item.productIndex, { refundApproved: true, refundApprovedByName: approverName });
      }
    } catch (err) {
      console.error('Error approving refund:', err);
    } finally {
      setProcessingId(null);
    }
  };

  // Handle reject refund
  const handleReject = async (item) => {
    if (!rejectionReason.trim()) return;
    
    setProcessingId(`${item.orderId}-${item.productIndex}`);
    try {
      const approverName = `${adminInfo.firstName} ${adminInfo.lastName}`;
      await request.patch(`/orders/${item.orderId}/products/${item.productIndex}/reject-refund`, {
        refundApproved: false,
        refundRejected: true,
        refundApprovedBy: adminInfo._id,
        refundApprovedByName: approverName,
        refundApprovedAt: new Date().toISOString(),
        refundRejectionReason: rejectionReason,
      });
      
      if (onRefundUpdated) {
        onRefundUpdated(item.orderId, item.productIndex, { 
          refundApproved: false, 
          refundRejected: true,
          refundRejectionReason: rejectionReason,
          refundApprovedByName: approverName 
        });
      }
      
      setShowRejectionModal(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error rejecting refund:', err);
    } finally {
      setProcessingId(null);
    }
  };

  // Only show for admins
  if (!isAdmin) {
    return null;
  }

  const pendingCount = refundItems.filter(item => item.refundApproved === undefined || item.refundApproved === null).length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-red-500 to-orange-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FiAlertCircle className="w-5 h-5" />
              Refund Approval Queue
            </h2>
            <p className="text-red-100 text-sm">
              {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Filter Buttons */}
          <div className="flex items-center gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f 
                    ? 'bg-white text-red-600' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'pending' && pendingCount > 0 && (
                  <span className="ml-1.5 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[500px] overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="p-12 text-center">
            <FiPackage className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No {filter === 'all' ? '' : filter} refunds</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredItems.map((item, idx) => {
              const isProcessing = processingId === `${item.orderId}-${item.productIndex}`;
              const isPending = item.refundApproved === undefined || item.refundApproved === null;
              
              return (
                <div key={`${item.orderId}-${item.productIndex}-${idx}`} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <div className="flex items-start gap-4">
                    {/* Product Image */}
                    <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0">
                      {item.product.image ? (
                        <img src={item.product.image} alt={item.product.name} className="w-full h-full object-contain" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FiPackage className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white line-clamp-1">
                            {item.product.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Order #{item.orderUniqueId} • ${parseFloat(item.product.price || 0).toFixed(2)}
                          </p>
                        </div>
                        
                        {/* Status Badge */}
                        <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                          item.refundApproved === true ? 'bg-emerald-100 text-emerald-700' :
                          item.refundApproved === false ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {item.refundApproved === true ? 'Approved' :
                           item.refundApproved === false ? 'Rejected' :
                           'Pending'}
                        </span>
                      </div>

                      {/* Refund Info */}
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <FiUser className="w-3 h-3" />
                          {item.refundedByName || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FiClock className="w-3 h-3" />
                          {item.refundedAt ? new Date(item.refundedAt).toLocaleString() : '–'}
                        </span>
                      </div>

                      {/* Reason */}
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {item.refundReason || 'No reason'}
                        </span>
                        {item.refundNotes && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">
                            "{item.refundNotes}"
                          </p>
                        )}
                      </div>

                      {/* Customer Preference Warning */}
                      {item.product.substituteOrRefund?.toLowerCase() === 'substitute' && (
                        <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <FiAlertCircle className="w-3 h-3" />
                          Customer preferred substitute
                        </div>
                      )}

                      {/* Rejection Reason */}
                      {item.refundRejectionReason && (
                        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <p className="text-xs text-red-700 dark:text-red-400">
                            <strong>Rejection reason:</strong> {item.refundRejectionReason}
                          </p>
                        </div>
                      )}

                      {/* Approval Info */}
                      {item.refundApproved !== undefined && item.refundApproved !== null && item.refundApprovedByName && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          {item.refundApproved ? 'Approved' : 'Rejected'} by {item.refundApprovedByName}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    {isPending && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleApprove(item)}
                          disabled={isProcessing}
                          className="p-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Approve refund"
                        >
                          <FiCheck className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setShowRejectionModal(item)}
                          disabled={isProcessing}
                          className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Reject refund"
                        >
                          <FiX className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Reject Refund
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {showRejectionModal.product.name}
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectionModal(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(showRejectionModal)}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
              >
                Reject Refund
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

